import React, { useEffect } from 'react';
import { css } from '@emotion/react';
import { type UnsavedEditChangesConfig } from '@frontend/api-call-intel';
import { isWeaveUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { CheckboxField, Modal, ModalControlModalProps, Text, useFormField } from '@frontend/design-system';
import { LOCAL_STORAGE_KEYS } from '../../utils/constant';
import { generateTrackingId } from '../../utils/tracking';

type UnsavedChangesModalProps = {
  modalProps: ModalControlModalProps;
  isDemoAccount?: boolean;
  unsavedEditChangesConfig: UnsavedEditChangesConfig;
  setUnsavedEditChangesConfig: (config: UnsavedEditChangesConfig) => void;
};

export const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  modalProps,
  isDemoAccount,
  unsavedEditChangesConfig,
  setUnsavedEditChangesConfig,
}) => {
  const { t } = useTranslation('analytics');
  const fieldProps = useFormField({
    type: 'checkbox',
    value: false,
  });

  const handleAction = (actionType: UnsavedEditChangesConfig['actionType']) => {
    if (actionType === 'CLOSE') {
      setUnsavedEditChangesConfig({ actionType: 'CLOSE', dontAskAgain: false, hasUnsavedChanges: true });
      localStorage.setItem(LOCAL_STORAGE_KEYS.CALL_INTEL_UNSAVED_CHANGES_MODAL_PREF, String(false));
    } else if (actionType === 'SAVE') {
      setUnsavedEditChangesConfig({ actionType: 'SAVE', hasUnsavedChanges: true });
    } else if (actionType === 'DISCARD') {
      setUnsavedEditChangesConfig({ actionType: 'DISCARD', hasUnsavedChanges: true });
    }
  };

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.CALL_INTEL_UNSAVED_CHANGES_MODAL_PREF, String(fieldProps.value));
    setUnsavedEditChangesConfig({ ...unsavedEditChangesConfig, dontAskAgain: fieldProps.value });
  }, [fieldProps.value]);

  return (
    <Modal
      {...modalProps}
      maxWidth={400}
      css={styles.modalWrapper}
      onClose={() => handleAction('CLOSE')}
      className='unsaved-changes-modal'
    >
      <Modal.Header onClose={() => handleAction('CLOSE')}>{t('Unsaved Changes')}</Modal.Header>

      <Modal.Body css={styles.body}>
        <div css={styles.bodyContent}>
          <Text>{t('Changes to the Call Insights have not been saved.')}</Text>
          <Text>{t('Would you like to discard or save changes before you leave?')}</Text>
          <CheckboxField
            {...fieldProps}
            name='dontAskAgain'
            label={t("Don't ask me again")}
            trackingId={generateTrackingId({
              component: 'unsaved-changes-modal',
              context: 'dont-ask-again-checkbox-click',
              isDemoAccount,
            })}
          />
        </div>
        <Modal.Actions
          disablePrimary={isWeaveUser()}
          primaryLabel={t('Save Changes')}
          primaryTrackingId={generateTrackingId({
            component: 'unsaved-changes-modal',
            context: 'save-changes-btn-click',
            isDemoAccount,
          })}
          secondaryLabel={t('Discard')}
          secondaryTrackingId={generateTrackingId({
            component: 'unsaved-changes-modal',
            context: 'discard-btn-click',
            isDemoAccount,
          })}
          onPrimaryClick={() => handleAction('SAVE')}
          onSecondaryClick={() => handleAction('DISCARD')}
        />
      </Modal.Body>
    </Modal>
  );
};

const styles = {
  modalWrapper: css`
    width: 100%;

    @media screen and (min-width: 648px) {
      width: 100%;
    }
  `,
  body: css`
    gap: ${theme.spacing(4)};

    footer {
      padding: ${theme.spacing(0)};
    }

    footer button:first-child {
      color: ${theme.colors.critical50};
    }
  `,
  bodyContent: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `,
};
